import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { getCurrencyLimits } from '@pages/Traders-Room/Deposit/helpers'
import {
  GetDepositCurrencyLimitsValue,
  useGetDepositCurrencyLimits,
} from '@pages/Traders-Room/Deposit/hooks/useGetDepositCurrencyLimits'

import { BaseModalType } from '@context/ModalsContext'

import { Modal } from '@ui/components/Modal'

import { PaymentProvider } from '@model/PaymentProviderDto'
import type { Wallet } from '@model/WalletDepositFormValues'

interface BaseModalProps {
  title: string
  terms: string[]
  close: () => void
}

export const TermsModal: React.FC<BaseModalProps> = ({ close, title, terms }) => {
  return (
    <Modal size='lg'>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Body>
        <Modal.Body.Text>
          <Modal.Body.List ordered>
            {terms.map((term, index) => (
              <Modal.Body.List.Item key={index}>{term}</Modal.Body.List.Item>
            ))}
          </Modal.Body.List>
        </Modal.Body.Text>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>Close</Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal>
  )
}

export const FundingDepositTermsModal: BaseModalType<{
  wallet?: Wallet
  paymentProvider?: PaymentProvider
}> = ({ wallet, paymentProvider, close }) => {
  const { t } = useTranslation()

  const { depositCurrencyLimits } = useGetDepositCurrencyLimits({
    wallet: wallet,
    paymentProvider: paymentProvider,
  })
  const formattedTerms = useMemo(
    (): string[] =>
      paymentProvider?.parameters?.termsConditions?.map(({ name }) =>
        wallet && paymentProvider
          ? walletDepositTermsConditionsMessageFormat(name, paymentProvider, depositCurrencyLimits)
          : name
      ) ?? [],
    [paymentProvider, wallet, depositCurrencyLimits]
  )

  return (
    <TermsModal
      terms={formattedTerms}
      close={close}
      title={t('Trading Account.General Terms and Conditions for Deposits')}
    />
  )
}

const walletDepositTermsConditionsMessageFormat = (
  message: string,
  paymentProvider: PaymentProvider,
  currencyLimitPassed?: GetDepositCurrencyLimitsValue
): string => {
  const currencyLimit = currencyLimitPassed ?? getCurrencyLimits(paymentProvider)
  const minAmount = currencyLimit?.minAmount?.toString() || '0'
  const maxAmount = currencyLimit?.maxAmount?.toString() || '0'

  const currency = paymentProvider.currency.id || ''
  return message
    .replace(/{minAmount}/g, minAmount)
    .replace(/{maxAmount}/g, maxAmount)
    .replace(/{paymentProviderCurrency}/g, currency)
}

export const TradingAccountDepositTermsModal: BaseModalType<{
  wallet?: Wallet
  paymentProvider?: PaymentProvider
}> = ({ wallet, paymentProvider, close }) => {
  const { t } = useTranslation()

  const { depositCurrencyLimits } = useGetDepositCurrencyLimits({
    wallet: wallet,
    paymentProvider: paymentProvider,
  })
  const formattedTerms = useMemo(
    (): string[] =>
      paymentProvider?.parameters?.termsConditions?.map(({ name }) =>
        wallet && paymentProvider
          ? walletDepositTermsConditionsMessageFormat(name, paymentProvider, depositCurrencyLimits)
          : name
      ) ?? [],
    [paymentProvider, wallet, depositCurrencyLimits]
  )

  return (
    <TermsModal
      terms={formattedTerms}
      close={close}
      title={t('Trading Account.General Terms and Conditions for Deposits')}
    />
  )
}
